import React, { useState } from 'react';
import { Mail, MessageCircle, MapPin, Send } from 'lucide-react';
import { SendEmailCommand } from "@aws-sdk/client-ses";
import { sesClient, AWS_CONFIG } from '../config/aws';

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    service: '',
    message: ''
  });

  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors: {[key: string]: string} = {};

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'El mensaje es requerido';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      if (!AWS_CONFIG.credentials.accessKeyId || !AWS_CONFIG.credentials.secretAccessKey) {
        throw new Error("AWS Credentials not configured in .env");
      }

      const command = new SendEmailCommand({
        Source: AWS_CONFIG.sourceEmail,
        Destination: {
          ToAddresses: [AWS_CONFIG.destinationEmail],
        },
        Message: {
          Subject: {
            Data: `Nueva consulta de ${formData.name} - UniLearn`,
          },
          Body: {
            Text: {
              Data: `
Nombre: ${formData.name}
Email: ${formData.email}
Empresa: ${formData.company}
Teléfono: ${formData.phone}
Servicio: ${formData.service}

Mensaje:
${formData.message}
              `,
            },
          },
        },
        ReplyToAddresses: [formData.email], // Allows replying directly to the user
      });

      await sesClient.send(command);
      
      alert('¡Gracias por tu mensaje! Te contactaremos pronto a través de tu email.');
      setFormData({
        name: '',
        email: '',
        company: '',
        phone: '',
        service: '',
        message: ''
      });
    } catch (error: any) {
      console.error('Error enviando email:', error);
      
      let errorMessage = 'Hubo un error enviando tu mensaje.';
      
      // Add technical details if available
      if (error?.message) {
        errorMessage += `\n\nDetalle técnico: ${error.message}`;
      }

      // Check for missing credentials
      if (!AWS_CONFIG.credentials.accessKeyId || AWS_CONFIG.credentials.accessKeyId === 'YOUR_AWS_ACCESS_KEY') {
        errorMessage += '\n\nCAUSA PROBABLE: Las credenciales de AWS en el archivo .env son las de ejemplo o faltan. Por favor configura REACT_APP_AWS_ACCESS_KEY_ID y REACT_APP_AWS_SECRET_ACCESS_KEY.';
      } else {
         errorMessage += '\n\nPor favor, intenta nuevamente o contactanos directamente a ' + AWS_CONFIG.destinationEmail;
      }

      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="contact">
      <div className="container">
        <div className="section-header">
          <h2 className="section-title">Contáctanos</h2>
          <p className="section-subtitle">
            ¿Listo para transformar tu educación digital? Hablemos sobre tu proyecto
          </p>
        </div>

        <div className="contact-content">
          <div className="contact-info">
            <h3>Información de Contacto</h3>
            <p>
              Nuestro equipo de expertos está listo para ayudarte a implementar 
              la solución Moodle perfecta para tu institución.
            </p>

            <div className="contact-details">
              <div className="contact-item">
                <div className="contact-icon">
                  <Mail />
                </div>
                <div className="contact-text">
                  <h4>Email</h4>
                  <p>contacto@unilearn.cl</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon whatsapp-icon">
                  <MessageCircle />
                </div>
                <div className="contact-text">
                  <h4>WhatsApp</h4>
                  <a 
                    href="https://wa.me/56992235151?text=Hola,%20me%20interesa%20conocer%20más%20sobre%20los%20servicios%20de%20Moodle" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="whatsapp-link"
                  >
                    Chatear ahora
                  </a>
                  <p>Lunes a Viernes: 9:00 - 18:00</p>
                </div>
              </div>

              <div className="contact-item">
                <div className="contact-icon">
                  <MapPin />
                </div>
                <div className="contact-text">
                  <h4>Oficina</h4>
                  <p>Cristóbal Colón 4947</p>
                  <p>Las Condes, Santiago</p>
                </div>
              </div>
            </div>

            <div className="response-time">
              <h4>Tiempo de Respuesta</h4>
              <ul>
                <li><strong>Consultas generales:</strong> Menos de 4 horas</li>
                <li><strong>Soporte técnico:</strong> Menos de 2 horas</li>
                <li><strong>Emergencias:</strong> Inmediato (clientes premium)</li>
              </ul>
            </div>
          </div>

          <div className="contact-form-container">
            <form className="contact-form" onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Nombre Completo *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className={errors.name ? 'error' : ''}
                  placeholder="Tu nombre completo"
                />
                {errors.name && <span className="error-message">{errors.name}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={errors.email ? 'error' : ''}
                  placeholder="tu@email.com"
                />
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="company">Institución/Empresa</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Nombre de tu institución"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">Teléfono / WhatsApp</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    placeholder="+56 9 1234 5678"
                  />
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="service">Servicio de Interés</label>
                <select
                  id="service"
                  name="service"
                  value={formData.service}
                  onChange={handleChange}
                >
                  <option value="">Selecciona un servicio</option>
                  <option value="wordpress-trial">🎉 WordPress Autogestionado - Prueba 30 días GRATIS</option>
                  <option value="hosting">Moodle Cloud Hosting</option>
                  <option value="administracion">Administración Completa</option>
                  <option value="migracion">Migración de Datos</option>
                  <option value="personalizacion">Personalización Avanzada</option>
                  <option value="soporte">Soporte Técnico</option>
                  <option value="capacitacion">Capacitación y Consultoría</option>
                  <option value="otro">Otro (especificar en mensaje)</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="message">Mensaje *</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  className={errors.message ? 'error' : ''}
                  placeholder="Cuéntanos sobre tu proyecto y necesidades específicas..."
                  rows={5}
                ></textarea>
                {errors.message && <span className="error-message">{errors.message}</span>}
              </div>

              <button 
                type="submit" 
                className="btn btn-primary btn-full"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  'Enviando...'
                ) : (
                  <>
                    Enviar Mensaje
                    <Send className="btn-icon" />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;